import './BillingConfiguration.scss';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdAdd, MdDelete, MdError } from 'react-icons/md';
import { RiPencilFill } from 'react-icons/ri';
import {
  accountKey,
  BillingAccount,
  BillingConfigRow,
  TVADBillingAccount,
  UpdateBillingConfigurationsBody,
  YouTubeBillingAccount
} from '@blisspointmedia/bpm-types/dist/BillingAutomation';
import { AlertDialog } from '@Components/AlertDialog';
import { Button } from '@Components/Button';
import { Checkbox } from '@Components/FormCheck';
import { Appearance, Design, Intent, Size } from '@Components/designConstants';
import { EmptyState } from '@Components/EmptyState';
import { ModalEditWidget } from '@Components/ModalEditWidget';
import {
  CustomModalEditWidgetHeader,
  ModalEditWidgetHandle,
  TableRow
} from '@Components/ModalEditWidget/modal-edit-constants';
import { IconButton } from '@src/Components/IconButton/IconButton';
import { MetricDropdownOption } from '@src/Components/MetricDropdown';
import { useSetError } from '../../redux/modals';
import { awaitJSON, BillingAutomationLambdaFetch } from '../../utils/fetch-utils';
import { AddConfigModal } from './AddConfigModal';
import {
  accountLabel,
  BILLING_METRIC_LABELS,
  DSP_OWNER_LABELS,
  DspOwnerType,
  getConfigAccountCategory,
  SPEND_TYPE_LABELS,
  SpendType
} from './billing-utils';
import { NotificationBanner } from './NotificationBanner';

const ROW_BASE_HEIGHT = 40;
const ROW_LINE_HEIGHT = 32;
const ACCOUNTS_PER_LINE = 2;

const estimateRowHeight = (row?: BillingConfigRow): number => {
  const accountCount = row?.accounts?.length ?? 0;
  const lines = Math.max(1, Math.ceil(accountCount / ACCOUNTS_PER_LINE));
  return ROW_BASE_HEIGHT + (lines - 1) * ROW_LINE_HEIGHT;
};

interface BillingConfigurationProps {
  company: string;
  userEmail: string;
}

export const BillingConfiguration = ({ company, userEmail }: BillingConfigurationProps) => {
  const setError = useSetError();
  const [masterData, setMasterData] = useState<BillingConfigRow[]>([]);
  const [metricDropdownOptions, setMetricDropdownOptions] = useState<{
    kpiOptions: MetricDropdownOption[];
    sourceOptions: MetricDropdownOption[];
  }>({ kpiOptions: [], sourceOptions: [] });
  const [fetchedAvailableAccounts, setFetchedAvailableAccounts] = useState<
    (BillingAccount | TVADBillingAccount | YouTubeBillingAccount)[]
  >([]);
  const [configState, setConfigState] = useState<{
    pendingAccounts: Partial<
      Record<string, (BillingAccount | TVADBillingAccount | YouTubeBillingAccount)[]>
    >;
    availableAccounts: (BillingAccount | TVADBillingAccount | YouTubeBillingAccount)[];
  }>({ pendingAccounts: {}, availableAccounts: [] });
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [editingRow, setEditingRow] = useState<BillingConfigRow | undefined>();
  const widgetRef = useRef<ModalEditWidgetHandle<BillingConfigRow>>(null);
  const [editingRowKey, setEditingRowKey] = useState<string | undefined>();
  const [rowPendingDelete, setRowPendingDelete] = useState<TableRow<BillingConfigRow> | null>(null);
  const [isFetching, setIsFetching] = useState(true);
  const [skipDeleteConfirm, setSkipDeleteConfirm] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const loadData = useCallback(async () => {
    // `company` defaults to '' in Redux before it hydrates; wait for a real value.
    if (!company) {
      return;
    }
    setIsFetching(true);
    try {
      const res = await BillingAutomationLambdaFetch('/fetchBillingConfigurations', {
        params: { company }
      });
      const {
        metricDropdownOptions: fetchedOptions,
        billingConfigRows,
        availableAccounts: accounts
      } = await awaitJSON<{
        metricDropdownOptions: {
          kpiOptions: MetricDropdownOption[];
          sourceOptions: MetricDropdownOption[];
        };
        billingConfigRows: BillingConfigRow[];
        availableAccounts: (BillingAccount | TVADBillingAccount | YouTubeBillingAccount)[];
      }>(res);
      setMetricDropdownOptions(fetchedOptions);
      setMasterData(billingConfigRows);
      setFetchedAvailableAccounts(accounts);
      setConfigState({ pendingAccounts: {}, availableAccounts: accounts });
    } catch (e) {
      setError({ message: e.message, title: 'Failed to load billing data', reportError: e });
    } finally {
      setIsFetching(false);
    }
  }, [setError, company]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const stageConfig = useCallback(
    (updatedRows: BillingConfigRow[]) => {
      if (editingRowKey !== undefined) {
        // A mixed-category edit splits into multiple rows. Keep the edited row on its
        // original category; any split of a different category becomes its own new row.
        // Rows are homogeneous and non-empty by construction (see AddConfigModal.handleSave),
        // so accounts[0] is a safe representative for classifying the whole row.
        const editingCategory = editingRow
          ? getConfigAccountCategory(editingRow.accounts[0])
          : undefined;
        const primaryIndex = Math.max(
          0,
          updatedRows.findIndex(r => getConfigAccountCategory(r.accounts[0]) === editingCategory)
        );
        const primary = updatedRows[primaryIndex];
        const rest = updatedRows.filter((_, i) => i !== primaryIndex);
        const newKeys = new Set(updatedRows.flatMap(r => r.accounts).map(accountKey));
        const prevAccounts = editingRow?.accounts ?? [];
        widgetRef.current?.trackModify(editingRowKey, primary);
        const restPending: Record<
          string,
          (BillingAccount | TVADBillingAccount | YouTubeBillingAccount)[]
        > = {};
        for (const extra of rest) {
          const newRowKey = widgetRef.current?.trackAdd(extra);
          if (newRowKey !== undefined) {
            restPending[newRowKey] = extra.accounts;
          }
        }
        setConfigState(prev => ({
          pendingAccounts: {
            ...prev.pendingAccounts,
            [editingRowKey]: primary.accounts,
            ...restPending
          },
          availableAccounts: [
            ...prev.availableAccounts.filter(a => !newKeys.has(accountKey(a))),
            ...prevAccounts.filter(a => !newKeys.has(accountKey(a)))
          ]
        }));
      } else {
        for (const updated of updatedRows) {
          const newRowKey = widgetRef.current?.trackAdd(updated);
          if (newRowKey !== undefined) {
            const newKeys = new Set(updated.accounts.map(accountKey));
            setConfigState(prev => ({
              pendingAccounts: { ...prev.pendingAccounts, [newRowKey]: updated.accounts },
              availableAccounts: prev.availableAccounts.filter(a => !newKeys.has(accountKey(a)))
            }));
          }
        }
      }
    },
    [editingRow, editingRowKey]
  );

  const commitDelete = useCallback((deleted: TableRow<BillingConfigRow>) => {
    widgetRef.current?.trackDelete(deleted.rowKey);
    setConfigState(prev => {
      const { [deleted.rowKey]: _, ...remainingPending } = prev.pendingAccounts;
      return {
        pendingAccounts: remainingPending,
        availableAccounts: [...prev.availableAccounts, ...deleted.accounts]
      };
    });
  }, []);

  const hasKpiConfig = useCallback((row: BillingConfigRow) => Boolean(row.kpi), []);

  const handleDeleteRow = useCallback(
    (row: TableRow<BillingConfigRow>) => {
      if (skipDeleteConfirm) {
        commitDelete(row);
      } else {
        setRowPendingDelete(row);
      }
    },
    [commitDelete, skipDeleteConfirm]
  );

  const billingHeaders: CustomModalEditWidgetHeader[] = useMemo(
    () => [
      {
        label: 'Accounts',
        field: 'accounts',
        flex: 2,
        optionsGetter: (row: BillingConfigRow) => (row.accounts || []).map(accountLabel),
        renderer: (row: BillingConfigRow) => (
          <div className='account-pills'>
            {(row.accounts || []).map(acct => (
              <span key={accountKey(acct)} className='account-pill'>
                {accountLabel(acct)}
              </span>
            ))}
          </div>
        )
      },
      {
        label: 'Billing Metric',
        field: 'billingMetric',
        flex: 1,
        type: 'select',
        renderer: (row: BillingConfigRow) => (
          <div className='billing-cell-centered'>
            <span>
              {row.billingMetric
                ? (BILLING_METRIC_LABELS[row.billingMetric] ?? row.billingMetric)
                : '—'}
            </span>
          </div>
        )
      },
      {
        label: 'KPI Name',
        field: 'kpiName',
        flex: 1,
        renderer: (row: BillingConfigRow) => (
          <div className='billing-cell-centered'>
            {hasKpiConfig(row) ? (
              <span>{row.kpi?.label}</span>
            ) : (
              <span className='config-empty-cell'>—</span>
            )}
          </div>
        )
      },
      {
        label: 'KPI Source',
        field: 'kpiSource',
        flex: 1,
        renderer: (row: BillingConfigRow) => (
          <div className='billing-cell-centered'>
            {hasKpiConfig(row) ? (
              <span>{row.source?.label ?? '—'}</span>
            ) : (
              <span className='config-empty-cell'>—</span>
            )}
          </div>
        )
      },
      {
        label: 'Spend Type',
        field: 'spendType',
        flex: 1,
        type: 'select',
        renderer: (row: BillingConfigRow) => (
          <div className='billing-cell-centered'>
            <span>
              {row.spend?.type
                ? (SPEND_TYPE_LABELS[row.spend.type as SpendType] ?? row.spend.type)
                : '—'}
            </span>
          </div>
        )
      },
      {
        label: 'DSP Seat Owner (TVAD-only)',
        field: 'dspSeatOwner',
        flex: 1,
        type: 'select',
        renderer: (row: BillingConfigRow) => (
          <div className='billing-cell-centered'>
            <span>
              {row.spend?.dspSeatOwner
                ? (DSP_OWNER_LABELS[row.spend.dspSeatOwner as DspOwnerType] ??
                  row.spend.dspSeatOwner)
                : '—'}
            </span>
          </div>
        )
      },
      {
        label: '',
        field: 'id',
        flex: 0,
        width: 96,
        hideInPendingChanges: true,
        renderer: (row: TableRow<BillingConfigRow>) => (
          <div className='config-action-buttons billing-cell-centered'>
            <IconButton
              design={Design.BRAND}
              appearance={Appearance.EMPTY}
              size={Size.SM}
              onClick={e => {
                e.stopPropagation();
                setEditingRow(row);
                setEditingRowKey(row.rowKey);
                setShowConfigModal(true);
              }}
            >
              <RiPencilFill />
            </IconButton>
            <IconButton
              design={Design.BRAND}
              appearance={Appearance.EMPTY}
              size={Size.SM}
              onClick={e => {
                e.stopPropagation();
                handleDeleteRow(row);
              }}
            >
              <MdDelete />
            </IconButton>
          </div>
        )
      }
    ],
    [hasKpiConfig, handleDeleteRow]
  );

  const configTable = () => {
    const openAddModal = () => {
      setEditingRow(undefined);
      setEditingRowKey(undefined);
      setShowConfigModal(true);
    };
    return (
      <ModalEditWidget<BillingConfigRow>
        ref={widgetRef}
        name='Billing Metric by Account'
        addButtonText='Add Accounts'
        className='config-table'
        headers={billingHeaders}
        initialTableData={masterData}
        isLoading={isFetching}
        selectorOptions={{}}
        emptyContent={
          <EmptyState
            title='No billing metrics yet.'
            subtitle='Add a row to start with the automated billing process.'
          >
            <Button size={Size.LG} leadingElement={<MdAdd />} onClick={openAddModal}>
              Add Account(s)
            </Button>
          </EmptyState>
        }
        customEditor={{
          onRowClick: row => {
            setEditingRow(row);
            setEditingRowKey(row.rowKey);
            setShowConfigModal(true);
          },
          onAddClick: openAddModal
        }}
        onSaveModal={async ({ addedData, modifiedData, deletedData, cleanup }) => {
          try {
            const body: UpdateBillingConfigurationsBody = {
              company,
              added: addedData,
              modified: modifiedData,
              deleted: deletedData,
              email: userEmail
            };
            await BillingAutomationLambdaFetch('/updateBillingConfigurations', {
              method: 'POST',
              body
            });
            cleanup();
            await loadData();
          } catch (e) {
            setError({
              message: e.message,
              title: 'Failed to save billing configuration',
              reportError: e
            });
          }
        }}
        onDiscard={() =>
          setConfigState({ pendingAccounts: {}, availableAccounts: fetchedAvailableAccounts })
        }
        rowHeightGetter={estimateRowHeight}
      />
    );
  };

  return (
    <>
      <div className='billing-configuration'>
        <div className='config-container'>
          <div className='config-container-body'>
            <NotificationBanner
              intent={Intent.INFO}
              message={
                <>
                  Select metrics below that will be used to generate bills for each account. Billing
                  sheets are generated automatically on the 1st of each month. Sheets will require
                  approval and then will be sent to Salesforce for invoicing.
                </>
              }
            />
            {configTable()}
          </div>
        </div>
      </div>
      {showConfigModal && (
        <AddConfigModal
          show={showConfigModal}
          onClose={() => setShowConfigModal(false)}
          onSave={stageConfig}
          metricDropdownOptions={metricDropdownOptions}
          availableAccounts={
            editingRow
              ? [...editingRow.accounts, ...configState.availableAccounts]
              : configState.availableAccounts
          }
          existingRow={editingRow}
        />
      )}
      <AlertDialog
        show={rowPendingDelete !== null}
        onHide={() => {
          setRowPendingDelete(null);
          setDontShowAgain(false);
        }}
        intent={Intent.ERROR}
        icon={<MdError size={40} />}
        title='Delete Row?'
        body='Are you sure you want to delete this row?'
        primaryAction={{
          onClick: () => {
            if (rowPendingDelete) {
              commitDelete(rowPendingDelete);
            }
            if (dontShowAgain) {
              setSkipDeleteConfirm(true);
            }
          },
          label: 'Delete'
        }}
        secondaryAction={{ onClick: () => {} }}
        additionalFooterContent={
          <Checkbox
            label="Don't show again"
            checked={dontShowAgain}
            onChange={e => setDontShowAgain(e.target.checked)}
          />
        }
      />
    </>
  );
};
